import clsx from 'clsx';
import { Avatar, Button, Dropdown, Modal, Space, Tooltip, Typography } from 'antd';
import { BaseKey, useDelete, useNotification } from '@refinedev/core';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  EditOutlined,
  EllipsisOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  PlayCircleOutlined
} from '@ant-design/icons';
import { IInteractiveClass } from 'interfaces/interactive_classes';
import { MenuProps } from 'antd/lib';
import { TagField } from '@refinedev/antd';
import {
  downloadReportFile,
  getStartUrlUnavailableReason,
  getStatusColor,
  isStartUrlAvailable,
  translateStatus
} from 'services/professor_area/interactive-classes';
import { formatDate } from 'services/date';
import { useCallback, useState } from 'react';

const { confirm } = Modal;

const formatDuration = (durationInMinutes: number) => {
  const hours = Math.floor(durationInMinutes / 60);
  const minutes = durationInMinutes % 60;

  if (hours === 0) {
    return `${minutes}min`;
  }

  if (minutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h${minutes}min`;
};

export interface TableRowItemProps {
  showModal: (isEdit: boolean, id?: BaseKey) => () => void;
  onWatchRecordings: (interactiveClass: IInteractiveClass) => void;
  interactiveClass: IInteractiveClass;
  refetch: () => void;
  canShow: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export const TableRowItem = ({
  showModal,
  refetch,
  onWatchRecordings,
  interactiveClass,
  canShow,
  canEdit,
  canDelete
}: TableRowItemProps) => {
  const [hovered, setHover] = useState(false);
  const notification = useNotification();
  const { mutateAsync: deleteInteractiveClass } =
    useDelete<IInteractiveClass>();

  const handleLinkAction = useCallback(() => {
    window.open(interactiveClass?.start_url);
  }, [interactiveClass?.start_url]);

  const confirmCancel = () => {
    confirm({
      title: 'Cancelar aula interativa?',
      icon: <ExclamationCircleFilled className="!text-[red]" />,
      content: 'Tem certeza que deseja cancelar a aula ?',
      okText: 'Cancelar',
      cancelText: 'Voltar',
      okButtonProps: { type: 'primary' },
      onOk() {
        deleteInteractiveClass({
          id: interactiveClass?.id,
          resource: 'professor_area/interactive_classes',
          successNotification: () => ({
            type: 'success',
            description: 'Sucesso!',
            message: 'Aula interativa cancelada com sucesso.'
          }),
          errorNotification: () => ({
            type: 'error',
            description: 'Ops!',
            message:
              'Não foi possível cancelar a aula interativa. Tente novamente.'
          })
        }).then(() => {
          refetch();
        });
      }
    });
  };

  const confirmJoinClass = () => {
    const canStartClass = Boolean(
      interactiveClass?.start_url &&
        isStartUrlAvailable(
          interactiveClass?.start_time,
          interactiveClass?.status
        )
    );
    const unavailableReason =
      interactiveClass?.start_time && interactiveClass?.status
        ? getStartUrlUnavailableReason(
            interactiveClass?.start_time,
            interactiveClass?.status
          )
        : 'A aula ainda não possui link de acesso';

    confirm({
      title: canStartClass
        ? 'Tem certeza que deseja iniciar a aula?'
        : 'Link indisponível',
      content: canStartClass ? '' : unavailableReason,
      icon: <ExclamationCircleFilled />,
      okText: canStartClass ? 'Iniciar aula' : 'Ok',
      cancelText: 'Voltar',
      okButtonProps: { type: 'primary' },
      onOk() {
        if (canStartClass) {
          handleLinkAction();
        }
      }
    });
  };

  const startClassDropdownItem: MenuProps['items'] =
    canShow && interactiveClass?.start_url
      ? [
          {
            key: '1',
            label: (
              <Button
                className="!text-[#000000E0]"
                type="link"
                icon={<PlayCircleOutlined className="!text-[#00000073]" />}
                onClick={() => confirmJoinClass()}
              >
                Iniciar aula
              </Button>
            )
          }
        ]
      : [];

  const defaultDropdownItems: MenuProps['items'] = [
    ...startClassDropdownItem,
    ...(canShow
      ? [
          {
            key: '2',
            label: (
              <Button
                className="!text-[#000000E0]"
                type="link"
                icon={<EyeOutlined className="!text-[#00000073]" />}
                onClick={showModal(false, interactiveClass.id)}
              >
                Ver dados da aula
              </Button>
            )
          }
        ]
      : []),
    ...(canEdit
      ? [
          {
            key: '3',
            label: (
              <Button
                className="!text-[#000000E0]"
                type="link"
                icon={<EditOutlined className="!text-[#00000073]" />}
                onClick={showModal(true, interactiveClass.id)}
              >
                Editar aula
              </Button>
            )
          }
        ]
      : []),
    ...(canDelete
      ? [
          {
            key: '4',
            label: (
              <Button
                className="!text-[#000000E0]"
                type="link"
                icon={<CloseCircleOutlined className="!text-[#00000073]" />}
                onClick={() => confirmCancel()}
              >
                Cancelar aula
              </Button>
            )
          }
        ]
      : [])
  ];

  const finishedDropdownItems: MenuProps['items'] = [
    ...(canShow
      ? [
          {
            key: '1',
            label: (
              <Button
                className="!text-[#000000E0]"
                type="link"
                icon={<EyeOutlined className="!text-[#00000073]" />}
                onClick={showModal(false, interactiveClass.id)}
              >
                Ver dados da aula
              </Button>
            )
          },
          {
            key: '2',
            label: (
              <Button
                className="!text-[#000000E0]"
                type="link"
                icon={<PlayCircleOutlined className="!text-[#00000073]" />}
                onClick={() => onWatchRecordings(interactiveClass)}
              >
                Assistir aula gravada
              </Button>
            )
          },
          {
            key: '3',
            label: (
              <Button
                className="!text-[#000000E0]"
                type="link"
                icon={<DownloadOutlined className="!text-[#00000073]" />}
                onClick={() =>
                  downloadReportFile(interactiveClass.id, notification)
                }
              >
                Baixar relatório de presença
              </Button>
            )
          }
        ]
      : [])
  ];
  const dropdownItems =
    interactiveClass.status === 'finished'
      ? finishedDropdownItems
      : defaultDropdownItems;

  return (
    <Space className="group flex w-full py-4 px-2 justify-between hover:bg-neutral-100">
      <Space className="!gap-4">
        <Space
          direction="vertical"
          className="flex rounded-md w-[56px] h-[56px] p-2 bg-[#E6F4FF] justify-center items-center !gap-0"
        >
          <Typography.Text className="!m-0 !text-[#1677FF]">
            {formatDate(interactiveClass.start_time, 'DD')}
          </Typography.Text>
          <Typography.Text className="!m-0 !text-[#1677FF] uppercase">
            {formatDate(interactiveClass.start_time, 'MMM')}
          </Typography.Text>
        </Space>
        <Space direction="vertical">
          <Typography.Title level={5} className="!m-0">
            {interactiveClass.title}
          </Typography.Title>
          <Space>
            <Space className="!text-[#000000A6]">
              <CalendarOutlined />
              <Typography.Text className="!m-0 !text-[#000000A6]">
                {formatDate(interactiveClass.start_time, 'DD/MM/YYYY HH:mm')}
              </Typography.Text>
            </Space>
            <Space className="!text-[#000000A6]">
              <ClockCircleOutlined />
              <Typography.Text className="!m-0 !text-[#000000A6]">
                {formatDuration(interactiveClass.duration_in_minutes)}
              </Typography.Text>
            </Space>
            <Space className="!gap-1">
              {interactiveClass.professors.map(professor => (
                <Tooltip key={professor.id} title={professor.name}>
                  <Avatar
                    size="small"
                    className="uppercase"
                    style={{ color: '#1677FF', backgroundColor: '#E6F4FF' }}
                  >
                    {professor.name
                      .split(' ', 2)
                      .map(word => word[0])
                      .join('')
                      .toUpperCase()}
                  </Avatar>
                </Tooltip>
              ))}
            </Space>
          </Space>
        </Space>
      </Space>
      <Space>
        <TagField
          value={translateStatus(interactiveClass.status)}
          color={getStatusColor(interactiveClass.status)}
        />
        <Space className="min-w-[40px]">
          {interactiveClass.status !== 'cancelled' &&
            dropdownItems.length > 0 && (
              <Dropdown
                menu={
                  {
                    items: dropdownItems
                  } as MenuProps
                }
                placement="bottomLeft"
                onOpenChange={setHover}
                className={clsx(
                  'opacity-0 group-hover:opacity-100 group-active:opacity-100',
                  hovered && 'opacity-100'
                )}
              >
                <Button icon={<EllipsisOutlined />} />
              </Dropdown>
            )}
        </Space>
      </Space>
    </Space>
  );
};
